import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { Button, Flex, Text } from "@hopper-ui/components";
import { PlusIcon } from "@hopper-ui/icons";
import { useCallback, useMemo, useState } from "react";

interface Preset {
    title: string;
    value: string;
}

interface Props {
    onSelect: (value: string) => void;
}

const shuffleArray = (array: Preset[]): Preset[] => {
    return [...array].sort(() => Math.random() - 0.5);
};

const MAX_VISIBLE_PRESETS = 5;

export const PersonalityPresets = ({ onSelect }: Props) => {
    const { tShared } = useSharedTranslation();
    const [showAll, setShowAll] = useState(false);

    const allPresets: Preset[] = [
        {
            title: tShared("personalityPresets.chatty.title"),
            value: tShared("personalityPresets.chatty.value")
        },
        {
            title: tShared("personalityPresets.playful.title"),
            value: tShared("personalityPresets.playful.value")
        },
        {
            title: tShared("personalityPresets.witty.title"),
            value: tShared("personalityPresets.witty.value")
        },
        {
            title: tShared("personalityPresets.supportive.title"),
            value: tShared("personalityPresets.supportive.value")
        },
        {
            title: tShared("personalityPresets.empathetic.title"),
            value: tShared("personalityPresets.empathetic.value")
        },
        {
            title: tShared("personalityPresets.curious.title"),
            value: tShared("personalityPresets.curious.value")
        },
        {
            title: tShared("personalityPresets.insightful.title"),
            value: tShared("personalityPresets.insightful.value")
        },
        {
            title: tShared("personalityPresets.analytical.title"),
            value: tShared("personalityPresets.analytical.value")
        },
        {
            title: tShared("personalityPresets.direct.title"),
            value: tShared("personalityPresets.direct.value")
        },
        {
            title: tShared("personalityPresets.professional.title"),
            value: tShared("personalityPresets.professional.value")
        },
        {
            title: tShared("personalityPresets.creative.title"),
            value: tShared("personalityPresets.creative.value")
        },
        {
            title: tShared("personalityPresets.patient.title"),
            value: tShared("personalityPresets.patient.value")
        }
    ];

    const [visiblePresets, setVisiblePresets] = useState<Preset[]>(shuffleArray(allPresets));

    // Handle preset selection
    const handlePresetClick = useCallback((preset: Preset) => {
        setVisiblePresets(prevPresets =>
            prevPresets.filter(p => p.title !== preset.title)
        );

        onSelect(preset.value);
    }, [onSelect]);

    const presetsToDisplay = useMemo(() => {
        return showAll ? visiblePresets : visiblePresets.slice(0, MAX_VISIBLE_PRESETS);
    }, [showAll, visiblePresets]);

    const showMoreButton = useMemo(() => {
        return visiblePresets.length > MAX_VISIBLE_PRESETS && !showAll;
    }, [visiblePresets, showAll]);

    return (
        <Flex
            gap="inline-sm"
            style={{ flexWrap: "wrap" }}
        >
            {presetsToDisplay.map(preset => (
                <Button
                    key={preset.title}
                    variant="secondary"
                    size="sm"
                    border="neutral-weak"
                    borderRadius="pill"
                    paddingLeft="inset-sm"
                    paddingRight="inset-md"
                    onPress={() => handlePresetClick(preset)}
                >
                    <PlusIcon size="sm" />
                    <Text>{preset.title}</Text>
                </Button>
            ))}
            {showMoreButton && (
                <Button
                    variant="secondary"
                    size="sm"
                    border="neutral-weak"
                    borderRadius="pill"
                    onPress={() => setShowAll(true)}
                >
                    <Text>{tShared("personalityPresets.more")}</Text>
                </Button>
            )}
        </Flex>
    );
};
